import { useCallback } from "react";
import { useHubSpotAdapterAxiosInstance } from "./useHubSpotAdapterAxiosInstance.ts";
import dayjs from "dayjs";
import { useWithLoading } from "../UI/Loading/useWithLoading.ts";

export type HubSpotCloserDTO = {
  id: string;
  email: string;
};

export type HubSpotBusyUser = {
  user: HubSpotCloserDTO;
  workCategory?: string;
  eventTitle?: string;
};

export type HubSpotCloserSlot = {
  slotStart: string;
  slotEnd: string;
  availableUsers: HubSpotCloserDTO[];
  busyWorkUsers: HubSpotBusyUser[];
  busyOooUsers: HubSpotBusyUser[];
};

export function useCheckHubSpotCloserAvailability() {
  const axiosInstance = useHubSpotAdapterAxiosInstance();
  const { withLoading, loading } = useWithLoading<HubSpotCloserSlot[]>();

  const checkAvailabilityCall = useCallback(
    async ({
      fromIncl,
      toIncl,
      filteredEmails,
    }: {
      fromIncl: Date;
      toIncl: Date;
      filteredEmails?: string[];
    }) => {
      const formattedFromIncl = dayjs(fromIncl).format();
      const formattedToIncl = dayjs(toIncl).format();

      const emailsParam =
        filteredEmails && filteredEmails.length > 0
          ? `&filteredEmails=${encodeURIComponent(filteredEmails.join(","))}`
          : "";

      const { data } = await axiosInstance.get<HubSpotCloserSlot[]>(
        `/closer-availability/get-enriched?fromIncl=${encodeURIComponent(formattedFromIncl)}&toIncl=${encodeURIComponent(formattedToIncl)}${emailsParam}`,
      );

      if (!Array.isArray(data)) {
        console.error("Unexpected HubSpot availability response:", data);
        throw new Error("Unexpected response format from HubSpot adapter");
      }

      return data;
    },
    [axiosInstance],
  );

  const checkHubSpotCloserAvailability = useCallback(
    async ({
      fromIncl,
      toIncl,
      filteredEmails,
    }: {
      fromIncl: Date;
      toIncl: Date;
      filteredEmails?: string[];
    }) => {
      return await withLoading(() =>
        checkAvailabilityCall({ fromIncl, toIncl, filteredEmails }),
      );
    },
    [withLoading, checkAvailabilityCall],
  );

  return {
    checkHubSpotCloserAvailability,
    loading,
  };
}
